import { amountsCloseEnough } from "./paymentScheduleStandardCount";

export function hasInstallmentSchedule(paymentPlan = {}) {
  const paymentCount = Number(paymentPlan.numPayments);

  return (
    (Number.isFinite(paymentCount) && paymentCount > 1) ||
    paymentPlan.paymentSchedule?.length > 1
  );
}

export function hasDistinctFinalPayment(
  standardPaymentAmount,
  lastPaymentAmount,
) {
  const standard = Number(standardPaymentAmount);
  const last = Number(lastPaymentAmount);

  if (!Number.isFinite(standard) || !Number.isFinite(last)) {
    return false;
  }

  return !amountsCloseEnough(standard, last);
}

export function getRegularPaymentCount(
  numPayments,
  standardPaymentAmount,
  lastPaymentAmount,
) {
  const totalPayments = Number(numPayments);

  if (!Number.isFinite(totalPayments) || totalPayments <= 0) {
    return 0;
  }

  if (hasDistinctFinalPayment(standardPaymentAmount, lastPaymentAmount)) {
    return Math.max(totalPayments - 1, 1);
  }

  return totalPayments;
}

const UPCOMING_PAYMENT_STATUSES = ["PENDING", "SCHEDULED"];

// The payment-plans API does not guarantee schedule rows arrive in date order.
export function sortPaymentScheduleByDate(paymentSchedule) {
  if (!Array.isArray(paymentSchedule)) return paymentSchedule;

  return [...paymentSchedule].sort((a, b) => {
    const aDate = String(a?.paymentDate ?? "");
    const bDate = String(b?.paymentDate ?? "");
    if (aDate === bDate) return 0;
    return aDate < bDate ? -1 : 1;
  });
}

const toCents = (amount) => Math.round(Number(amount) * 100);

// Consecutive date-ordered payments of the same cent amount.
function groupConsecutiveAmounts(cents) {
  return cents.reduce((groups, value) => {
    const current = groups.at(-1);
    if (current && current.cents === value) {
      current.count += 1;
    } else {
      groups.push({ cents: value, count: 1 });
    }
    return groups;
  }, []);
}

// Returns { first, regular, last } when the runs are exactly an optional single
// first payment, a run of regular payments, and an optional single final
// payment; otherwise null.
function matchFirstRegularFinal(groups) {
  const [a, b, c] = groups;

  if (groups.length === 1) return { first: null, regular: a, last: null };

  if (groups.length === 2) {
    // A two-payment plan with different amounts stays "N x regular + final".
    if (a.count === 1 && b.count === 1) {
      return { first: null, regular: a, last: b };
    }
    if (a.count === 1) return { first: a, regular: b, last: null };
    if (b.count === 1) return { first: null, regular: a, last: b };
    return null;
  }

  if (groups.length === 3 && a.count === 1 && c.count === 1) {
    return { first: a, regular: b, last: c };
  }

  return null;
}

// Interprose plans can carry odd-sized payments anywhere in the schedule, so
// the summary is read from the dated schedule rather than from paymentSize
// plus the last row. `paymentGroups` always reconstructs the upcoming rows;
// when they do not fit "first + N x regular + final", the schedule is
// reported as irregular and only the groups are meaningful.
export function summarizeUpcomingSchedule(paymentSchedule) {
  if (!Array.isArray(paymentSchedule)) return null;

  const cents = sortPaymentScheduleByDate(paymentSchedule)
    .filter(
      (payment) =>
        !payment?.paymentStatus ||
        UPCOMING_PAYMENT_STATUSES.includes(payment.paymentStatus),
    )
    .map((payment) => Number(payment?.paymentAmount))
    .filter(Number.isFinite)
    .map(toCents);

  if (!cents.length) return null;

  const groups = groupConsecutiveAmounts(cents);
  const paymentGroups = groups.map(({ cents: value, count }) => ({
    amount: value / 100,
    count,
  }));
  const match = matchFirstRegularFinal(groups);
  const toAmount = (group) => (group ? group.cents / 100 : null);

  if (!match) {
    return {
      isIrregular: true,
      paymentGroups,
      firstPaymentAmount: null,
      standardPaymentAmount: null,
      regularPaymentCount: null,
      lastPaymentAmount: null,
      upcomingPaymentCount: cents.length,
    };
  }

  return {
    isIrregular: false,
    paymentGroups,
    firstPaymentAmount: toAmount(match.first),
    standardPaymentAmount: toAmount(match.regular),
    regularPaymentCount: match.regular.count,
    lastPaymentAmount: toAmount(match.last),
    upcomingPaymentCount: cents.length,
  };
}

// Date-ordered runs of a portal `{ date, amount }` schedule. Runs are tagged
// "initial" / "final" only when the schedule is an optional single initial
// payment, a regular run and an optional single final payment, the same
// shapes the home cards describe with First / Final rows; any other schedule
// is irregular and no run is tagged.
export function describePaymentScheduleRuns(paymentSchedule) {
  if (!Array.isArray(paymentSchedule)) return null;

  const cents = [...paymentSchedule]
    .sort((a, b) => {
      const aDate = String(a?.date ?? "");
      const bDate = String(b?.date ?? "");
      if (aDate === bDate) return 0;
      return aDate < bDate ? -1 : 1;
    })
    .map((payment) => toCents(payment?.amount))
    .filter(Number.isFinite);

  if (!cents.length) return null;

  const groups = groupConsecutiveAmounts(cents);
  const match = matchFirstRegularFinal(groups);
  const roleOf = (group) => {
    if (!match) return null;
    if (group === match.first) return "initial";
    if (group === match.last) return "final";
    return "regular";
  };

  return {
    isIrregular: !match,
    runs: groups.map((group) => ({
      role: roleOf(group),
      count: group.count,
      amount: group.cents / 100,
    })),
  };
}

export function getPlanScheduleDisplay(
  numPayments,
  standardPaymentAmount,
  lastPaymentAmount,
) {
  const paymentCount = Number(numPayments);
  const validPaymentCount =
    Number.isFinite(paymentCount) && paymentCount > 0 ? paymentCount : 0;
  const hasFinalPayment =
    validPaymentCount > 0 &&
    hasDistinctFinalPayment(standardPaymentAmount, lastPaymentAmount);

  return {
    equalPaymentCount: hasFinalPayment ? 0 : validPaymentCount,
    hasFinalPayment,
    regularPaymentCount: hasFinalPayment
      ? getRegularPaymentCount(
          validPaymentCount,
          standardPaymentAmount,
          lastPaymentAmount,
        )
      : validPaymentCount,
  };
}
